import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Role } from "@/shared/types/role";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useCreateUser } from "./use-create-user";
import {
  CREATABLE_ROLES,
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  EMAIL_MAX_LENGTH,
} from "./constants";
import type { UserDetail } from "./types";

interface CreateUserDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated: (user: UserDetail) => void;
}

const DEFAULT_ROLE: Role = Role.WANDERFIT_SUPPORT;

// Conservative email syntax check — matches the validation the backend
// will run anyway. Not trying to be RFC 5322 compliant; just catching
// the obviously wrong cases before the round trip.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CreateUserDialog({ open, onOpenChange, onCreated }: CreateUserDialogProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>(DEFAULT_ROLE);
  const [sendPasswordResetEmail, setSendPasswordResetEmail] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mutation = useCreateUser();

  useEffect(() => {
    if (open) {
      setEmail("");
      setDisplayName("");
      setRole(DEFAULT_ROLE);
      setSendPasswordResetEmail(true);
      setError(null);
    }
  }, [open]);

  const trimmedEmail = email.trim();
  const trimmedName = displayName.trim();
  const isEmailValid = EMAIL_PATTERN.test(trimmedEmail);
  const isNameValid =
    trimmedName.length >= DISPLAY_NAME_MIN_LENGTH && trimmedName.length <= DISPLAY_NAME_MAX_LENGTH;
  const canSubmit = isEmailValid && isNameValid && !mutation.isPending;

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    setError(null);
    mutation.mutate(
      {
        email: trimmedEmail,
        displayName: trimmedName,
        role,
        sendPasswordResetEmail,
      },
      {
        onSuccess: (user) => {
          onOpenChange(false);
          onCreated(user);
        },
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "users.errors.forbidden",
              serverPrefixKey: "users.errors.serverPrefix",
              genericKey: "users.create.error",
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>{t("users.create.title")}</DialogTitle>
          <DialogDescription>{t("users.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label
              htmlFor="create-user-email"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("users.create.emailLabel")}
            </label>
            <Input
              id="create-user-email"
              data-testid="create-user-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={EMAIL_MAX_LENGTH}
              autoComplete="off"
              placeholder={t("users.create.emailPlaceholder")}
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="create-user-name"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("users.create.displayNameLabel")}
            </label>
            <Input
              id="create-user-name"
              data-testid="create-user-name-input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              autoComplete="off"
              placeholder={t("users.create.displayNamePlaceholder")}
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="create-user-role"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("users.create.roleLabel")}
            </label>
            <select
              id="create-user-role"
              data-testid="create-user-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {CREATABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`users.roleLabel.${r}`)}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2">
            <input
              data-testid="create-user-send-reset-checkbox"
              type="checkbox"
              checked={sendPasswordResetEmail}
              onChange={(e) => setSendPasswordResetEmail(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="font-sans text-sm font-normal text-foreground">
              {t("users.create.sendResetLabel")}
            </span>
          </label>

          {error && (
            <p
              data-testid="create-user-error"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("users.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-create-user-button"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {mutation.isPending ? t("users.create.pending") : t("users.create.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
